import { apiClient } from '../utils/apiClient';

export interface MasterCountryDto {
  myRowId?: number;
  id?: number;
  ssName?: string;
  ssCountry?: string;
  ssIsdCode?: string;
}

export interface MasterGenderDto {
  myRowId?: number;
  id?: number;
  ssName?: string;
}

export interface MasterTitleDto {
  myRowId?: number;
  id?: number;
  ssName?: string;
  ssTitleId?: string;
}

export interface MasterLookupDto {
  myRowId?: number;
  id?: number;
  name?: string;
  /** Dataverse GUID for the row when applicable (e.g. ss_countryid / ss_nationalityid). */
  externalId?: string;
}

export interface MasterStateDto {
  myRowId?: number;
  id?: number;
  ssName?: string;
  ssState?: string;
  ssStateId?: string;
  ssCountryValue?: string;
}

export interface MarketTypeDto {
  myRowId?: number;
  id?: number;
  marketName?: string;
  status?: boolean;
}

export interface IsdCodeValuesDto {
  myRowId?: number;
  id?: number;
  codeValue?: number;
  countryName?: string;
  countryCode?: string;
  nationality?: string;
  status?: number;
}

export interface MasterPortfolioManagersDto {
  id?: number;
  ssName?: string;
  ssPortfolioManagerId?: string;
  ssServiceProviderType?: string;
  ssNameOfTheFirmValue?: string;
  /** Resolved firm display name from master_accounts.name. Prefer this in UI. */
  firmName?: string;
}

export interface MasterPmsPlansDto {
  id?: number;
  ssName?: string;
  ssInvestmentRouteValue?: string;
  ssPlanDescription?: string;
  versionNumber?: string;
  ssPlanId?: string;
  ssProductValue?: string;
  ssPmsValue?: string;
  ssPreferredBankValue?: string;
  ssSchemeValue?: string;
  /** Resolved preferred bank display name (from master_accounts via _ss_preferredbank_value). */
  preferredBankName?: string;
}

export interface MasterPmsBanksDto {
  id?: number;
  ssName?: string;
  ssPmsBankId?: string;
  ssPortfolioManagerValue?: string;
  ssBankValue?: string;
  /** Resolved bank display name (from master_accounts via ss_bank_value). */
  bankName?: string;
}

class ContentService {
  private baseUrl = '/api/clients/content';

  async getCountries() {
    const response = await apiClient.get<MasterCountryDto[]>(
      `${this.baseUrl}/master/countries`
    );
    return response.data;
  }

  /** States for a country. `country` accepts the country's ss_countryid or its name. */
  async getStates(country?: string) {
    const response = await apiClient.get<MasterStateDto[]>(
      `${this.baseUrl}/master/states`,
      { params: country ? { country } : undefined }
    );
    return response.data;
  }

  async getGenders() {
    const response = await apiClient.get<MasterGenderDto[]>(
      `${this.baseUrl}/master/genders`
    );
    return response.data;
  }

  async getTitles() {
    const response = await apiClient.get<MasterTitleDto[]>(
      `${this.baseUrl}/master/titles`
    );
    return response.data;
  }

  async getNationalities() {
    const response = await apiClient.get<MasterLookupDto[]>(
      `${this.baseUrl}/master/nationalities`
    );
    return response.data;
  }

  /**
   * Country-of-residence list from master_country_of_residence. Each row's `externalId` is the
   * Dataverse GUID (ss_countryid) — the value the introduced-registration step 4 sends back
   * (the backend resolves it via findBySsCountryId). Distinct from getCountries() (master_countries).
   */
  async getCountryOfResidence() {
    const response = await apiClient.get<MasterLookupDto[]>(
      `${this.baseUrl}/master/country-of-residence`
    );
    return response.data;
  }

  async getMarketTypes() {
    const response = await apiClient.get<MarketTypeDto[]>(
      `${this.baseUrl}/master/market-types`
    );
    return response.data;
  }

  async getIsdCodes() {
    const response = await apiClient.get<IsdCodeValuesDto[]>(
      `${this.baseUrl}/master/isd-codes`
    );
    // Alphabetical by country, once, here. The API sorts by dialling code, which was fine while
    // only six countries were seeded but is unusable now that every country has a row (~214):
    // nobody looks for Germany by guessing it sits near +49. Several screens used to re-sort this
    // themselves and several forgot to, so the order differed between them.
    return [...response.data].sort((a, b) =>
      (a.countryName || '').localeCompare(b.countryName || '', undefined, { sensitivity: 'base' })
    );
  }

  async getPmsManagers() {
    const response = await apiClient.get<MasterPortfolioManagersDto[]>(
      `${this.baseUrl}/master/pms-managers`
    );
    return response.data;
  }

  async getPmsPlans() {
    const response = await apiClient.get<MasterPmsPlansDto[]>(
      `${this.baseUrl}/master/pms-plans`
    );
    return response.data;
  }

  async getPmsBanks() {
    const response = await apiClient.get<MasterPmsBanksDto[]>(
      `${this.baseUrl}/master/pms-banks`
    );
    return response.data;
  }
}

export const contentService = new ContentService();
